import type { TtqSession } from '../../services/ttqService';

/**
 * 画布颗粒显示编号：
 * - 原始：`1`、`2`…
 * - 一对多衍生：`1.1`、`1.2`…（挂在根编号下）
 * - 多对一收敛（cttq）：`cttq.1`、`cttq.2`…（独立序列，不隶属源颗粒）
 * - 多对一一解多题（pattern_unify）：`anq.1`、`anq.2`…
 * 删除不回收，允许空洞。
 */
export type TtqParticleNumberBook = {
  byId: Record<string, string>;
  nextRoot: number;
  /** 根颗粒 id → 下一个子序号（从 1 起） */
  nextChildByRoot: Record<string, number>;
  /** 下一个收敛序号（从 1 起）→ 显示为 cttq.N */
  nextCttq: number;
  /** 下一个一解多题序号（从 1 起）→ 显示为 anq.N */
  nextAnq: number;
};

export function emptyParticleNumberBook(): TtqParticleNumberBook {
  return { byId: {}, nextRoot: 1, nextChildByRoot: {}, nextCttq: 1, nextAnq: 1 };
}

function isCttqDisplayLabel(label: string): boolean {
  return /^cttq\.\d+$/i.test(String(label || '').trim());
}

function isAnqDisplayLabel(label: string): boolean {
  return /^anq\.\d+$/i.test(String(label || '').trim());
}

export function normalizeParticleNumberBook(
  raw?: Partial<TtqParticleNumberBook> | null,
): TtqParticleNumberBook {
  const byId: Record<string, string> = {};
  if (raw?.byId && typeof raw.byId === 'object') {
    for (const [id, value] of Object.entries(raw.byId)) {
      const key = String(id || '').trim();
      const label = String(value || '').trim();
      if (key && label) byId[key] = label;
    }
  }
  const nextRoot = Number.isFinite(raw?.nextRoot) && (raw!.nextRoot as number) >= 1
    ? Math.floor(raw!.nextRoot as number)
    : 1;
  const nextChildByRoot: Record<string, number> = {};
  if (raw?.nextChildByRoot && typeof raw.nextChildByRoot === 'object') {
    for (const [id, value] of Object.entries(raw.nextChildByRoot)) {
      const key = String(id || '').trim();
      const n = Number(value);
      if (key && Number.isFinite(n) && n >= 1) nextChildByRoot[key] = Math.floor(n);
    }
  }
  let nextCttq = Number.isFinite(raw?.nextCttq) && (raw!.nextCttq as number) >= 1
    ? Math.floor(raw!.nextCttq as number)
    : 1;
  let nextAnq = Number.isFinite(raw?.nextAnq) && (raw!.nextAnq as number) >= 1
    ? Math.floor(raw!.nextAnq as number)
    : 1;
  for (const label of Object.values(byId)) {
    const cttqMatch = /^cttq\.(\d+)$/i.exec(label);
    if (cttqMatch) {
      nextCttq = Math.max(nextCttq, Number(cttqMatch[1]) + 1);
    }
    const anqMatch = /^anq\.(\d+)$/i.exec(label);
    if (anqMatch) {
      nextAnq = Math.max(nextAnq, Number(anqMatch[1]) + 1);
    }
  }
  return { byId, nextRoot, nextChildByRoot, nextCttq, nextAnq };
}

function isCttqConvergeRun(run: NonNullable<TtqSession['pathRuns']>[number]): boolean {
  return run.pathId === 'cttq_converge' || run.ttqAngleId === 'cttq_converge';
}

function isPatternUnifyRun(run: NonNullable<TtqSession['pathRuns']>[number]): boolean {
  return run.pathId === 'pattern_unify' || run.ttqAngleId === 'pattern_unify';
}

/** 多对一产出（CTTQ 收敛 + 一解多题综合同解） */
export function isManyToOneProductRun(run: NonNullable<TtqSession['pathRuns']>[number]): boolean {
  return isCttqConvergeRun(run) || isPatternUnifyRun(run);
}

/** 多对一产出的代表/综合题颗粒 ID（仅应出现在鸟瞰，不进一对多主画布） */
export function collectCttqConvergeParticleIds(session: TtqSession): Set<string> {
  const ids = new Set<string>();
  for (const run of session.pathRuns ?? []) {
    if (run.status !== 'done' || !isManyToOneProductRun(run)) continue;
    for (const rawId of run.derivedParticleIds ?? []) {
      const id = String(rawId || '').trim();
      if (id) ids.add(id);
    }
  }
  return ids;
}

function buildDerivedToSource(session: TtqSession): Map<string, string> {
  const derivedToSource = new Map<string, string>();
  const doneRuns = (session.pathRuns ?? [])
    .filter((run) => run.status === 'done')
    .slice()
    .sort((a, b) => String(a.completedAt || a.startedAt || '').localeCompare(String(b.completedAt || b.startedAt || '')));

  for (const run of doneRuns) {
    if (isManyToOneProductRun(run)) continue;
    const sourceId = String(run.sourceParticleId || '').trim()
      || (run.sourceParticleIds ?? []).map((id) => String(id || '').trim()).find(Boolean)
      || '';
    if (!sourceId) continue;
    for (const rawId of run.derivedParticleIds ?? []) {
      const derivedId = String(rawId || '').trim();
      if (!derivedId || derivedId === sourceId) continue;
      if (!derivedToSource.has(derivedId)) {
        derivedToSource.set(derivedId, sourceId);
      }
    }
  }
  return derivedToSource;
}

/** 沿派生链上溯到原始根（用于一对多子序号：1.1 / 1.2 …） */
function resolveNumberRootId(
  particleId: string,
  derivedToSource: Map<string, string>,
): string {
  let current = particleId;
  const seen = new Set<string>();
  while (derivedToSource.has(current)) {
    if (seen.has(current)) break;
    seen.add(current);
    const parent = derivedToSource.get(current);
    if (!parent || parent === current) break;
    current = parent;
  }
  return current;
}

function listDerivedIdsInStableOrder(
  session: TtqSession,
  derivedToSource: Map<string, string>,
): string[] {
  const ordered: string[] = [];
  const seen = new Set<string>();
  const doneRuns = (session.pathRuns ?? [])
    .filter((run) => run.status === 'done')
    .slice()
    .sort((a, b) => String(a.completedAt || a.startedAt || '').localeCompare(String(b.completedAt || b.startedAt || '')));

  for (const run of doneRuns) {
    for (const rawId of run.derivedParticleIds ?? []) {
      const id = String(rawId || '').trim();
      if (!id || seen.has(id) || !derivedToSource.has(id)) continue;
      seen.add(id);
      ordered.push(id);
    }
  }

  for (const id of derivedToSource.keys()) {
    if (seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }
  return ordered;
}

function listConvergeIdsInStableOrder(session: TtqSession): Array<{ id: string; kind: 'cttq' | 'anq' }> {
  const ordered: Array<{ id: string; kind: 'cttq' | 'anq' }> = [];
  const seen = new Set<string>();
  const doneRuns = (session.pathRuns ?? [])
    .filter((run) => run.status === 'done' && isManyToOneProductRun(run))
    .slice()
    .sort((a, b) => String(a.completedAt || a.startedAt || '').localeCompare(String(b.completedAt || b.startedAt || '')));

  for (const run of doneRuns) {
    const kind: 'cttq' | 'anq' = isPatternUnifyRun(run) ? 'anq' : 'cttq';
    for (const rawId of run.derivedParticleIds ?? []) {
      const id = String(rawId || '').trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ordered.push({ id, kind });
    }
  }
  return ordered;
}

/**
 * 仅为尚未正确编号的颗粒分配新号。
 * - 收敛粒固定 `cttq.N` / 一解多题固定 `anq.N`
 * - 其它编号一旦正确落定则不回收、不改动
 */
export function ensureParticleNumbers(
  session: TtqSession,
  previous?: TtqParticleNumberBook | null,
): TtqParticleNumberBook {
  const book = normalizeParticleNumberBook(previous);
  const derivedToSource = buildDerivedToSource(session);
  const convergeIds = collectCttqConvergeParticleIds(session);
  const particles = session.particles ?? [];
  const presentIds = new Set(particles.map((entry) => String(entry.particleId || '').trim()).filter(Boolean));

  for (const item of listConvergeIdsInStableOrder(session)) {
    if (!presentIds.has(item.id)) continue;
    const existing = book.byId[item.id];
    if (item.kind === 'anq') {
      if (existing && isAnqDisplayLabel(existing)) continue;
      book.byId[item.id] = `anq.${book.nextAnq}`;
      book.nextAnq += 1;
      continue;
    }
    if (existing && isCttqDisplayLabel(existing)) continue;
    book.byId[item.id] = `cttq.${book.nextCttq}`;
    book.nextCttq += 1;
  }

  const roots = particles
    .map((entry) => String(entry.particleId || '').trim())
    .filter((id) => id && !derivedToSource.has(id) && !convergeIds.has(id));

  for (const rootId of roots) {
    if (book.byId[rootId]) continue;
    book.byId[rootId] = String(book.nextRoot);
    book.nextRoot += 1;
    if (!book.nextChildByRoot[rootId]) book.nextChildByRoot[rootId] = 1;
  }

  const assignRootIfMissing = (rootId: string) => {
    if (book.byId[rootId]) return;
    book.byId[rootId] = String(book.nextRoot);
    book.nextRoot += 1;
    if (!book.nextChildByRoot[rootId]) book.nextChildByRoot[rootId] = 1;
  };

  for (const derivedId of listDerivedIdsInStableOrder(session, derivedToSource)) {
    if (!presentIds.has(derivedId) || convergeIds.has(derivedId)) continue;
    if (book.byId[derivedId]) continue;
    const rootId = resolveNumberRootId(derivedId, derivedToSource);
    assignRootIfMissing(rootId);
    const rootLabel = book.byId[rootId];
    const childSeq = book.nextChildByRoot[rootId] ?? 1;
    book.byId[derivedId] = `${rootLabel}.${childSeq}`;
    book.nextChildByRoot[rootId] = childSeq + 1;
  }

  return book;
}

export function particleNumbersEqual(
  a?: TtqParticleNumberBook | null,
  b?: TtqParticleNumberBook | null,
): boolean {
  const left = normalizeParticleNumberBook(a);
  const right = normalizeParticleNumberBook(b);
  if (left.nextRoot !== right.nextRoot) return false;
  if (left.nextCttq !== right.nextCttq) return false;
  if (left.nextAnq !== right.nextAnq) return false;
  const leftIds = Object.keys(left.byId);
  const rightIds = Object.keys(right.byId);
  if (leftIds.length !== rightIds.length) return false;
  for (const id of leftIds) {
    if (left.byId[id] !== right.byId[id]) return false;
  }
  const leftRoots = Object.keys(left.nextChildByRoot);
  const rightRoots = Object.keys(right.nextChildByRoot);
  if (leftRoots.length !== rightRoots.length) return false;
  for (const id of leftRoots) {
    if (left.nextChildByRoot[id] !== right.nextChildByRoot[id]) return false;
  }
  return true;
}
